import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useGdOrchestration from '../hooks/useGdOrchestration';
import { useSessionHeartbeat } from '../hooks/useSessionHeartbeat';
import gdApi, { GdSessionFeedbackResponse } from '../api/gdApi';
import GdFeedbackDashboard from '../features/interview/components/GdFeedbackDashboard';
import DotGrid from '../components/common/DotGrid';
import { API_CONFIG } from '../utils/constants';
import { vibeService } from '../services/VibeService';
import { DEFAULT_MOCK_INTERVIEW_TOKEN } from '../features/interview/constants';
import { useAppSelector } from '../store';
import { onboardingApi } from '../api/onboardingApi';
import './GroupDiscussion.css';

interface GdTopic {
  id: string;
  title: string;
  category: string;
  difficulty: string;
  description: string;
}

const GroupDiscussion: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const navigationState = location.state as { token?: string; sessionId?: string } | null;
  const token = navigationState?.token ?? DEFAULT_MOCK_INTERVIEW_TOKEN;

  const [status, setStatus] = useState('Ready to start Group Discussion');
  const [isStarting, setIsStarting] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<GdTopic | null>(null);
  const [userName, setUserName] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showSubmitHint, setShowSubmitHint] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [useBackendTranscription, setUseBackendTranscription] = useState(false);
  const [topics, setTopics] = useState<GdTopic[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topicsError, setTopicsError] = useState<string>('');
  const [stableSpeaker, setStableSpeaker] = useState<string | null>(null);
  const stableSpeakerTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const [tipIndex, setTipIndex] = useState(0);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [feedbackData, setFeedbackData] = useState<GdSessionFeedbackResponse | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>('');
  const preAudioTips = useMemo(
    () => [
      'Gather your points!',
      'Be mindful...',
      'Have deeper discussions.',
      'Keep it concise.',
      'Listen actively.',
    ],
    []
  );

  const recognitionRef = React.useRef<any>(null);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const audioStreamRef = React.useRef<MediaStream | null>(null);
  const scriptProcessorRef = React.useRef<ScriptProcessorNode | null>(null);

  // Check if browser supports Web Speech API
  React.useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setUseBackendTranscription(true);
      console.log('⚠️ Browser does not support Web Speech API, will use backend transcription');
    }
  }, []);

  // Pre-fill display name: email local-part immediately, then profile first/last when loaded (Analytics pattern)
  useEffect(() => {
    if (!user?.email) return;
    const emailPart = user.email.split('@')[0]?.trim() || '';
    setUserName((prev) => (prev.trim() ? prev : emailPart));

    if (!user.id) return;
    let cancelled = false;
    const loadDisplayName = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        if (cancelled) return;
        const { first_name, last_name } = profileResponse.data;
        const fn = first_name?.trim() || '';
        const ln = last_name?.trim() || '';
        let resolved = '';
        if (fn && ln) resolved = `${fn} ${ln}`;
        else if (fn) resolved = fn;
        else if (ln) resolved = ln;
        else resolved = emailPart;
        setUserName((prev) => {
          const p = prev.trim();
          // Keep a custom edit; upgrade only from empty or the auto email placeholder
          if (p && p !== emailPart) return prev;
          return resolved;
        });
      } catch {
        if (cancelled) return;
        setUserName((prev) => (prev.trim() ? prev : emailPart));
      }
    };
    loadDisplayName();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.email]);

  // Fetch topics from API
  useEffect(() => {
    const fetchTopics = async () => {
      setLoadingTopics(true);
      vibeService.trigger('loading');
      setTopicsError('');
      
      try {
        // Get auth token from localStorage (not navigation token)
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${API_CONFIG.BASE_URL}gd/topics`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed. Please log in again.');
          }
          throw new Error(`Failed to fetch topics: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && Array.isArray(data.topics)) {
          setTopics(data.topics);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error: any) {
        console.error('Failed to fetch topics:', error);
        setTopicsError(error.message || 'Failed to load topics');
      } finally {
        setLoadingTopics(false);
        vibeService.dismiss();
      }
    };

    fetchTopics();
  }, []);

  const gd = useGdOrchestration({
    onStatus: setStatus,
    onError: (err) => setStatus(`❌ ${err}`),
  });

  // Keep auth token fresh during long GD sessions.
  // Without this, the token can expire mid-session since no HTTP requests
  // are made (all communication is via WebSocket). The heartbeat sends
  // periodic /auth/me pings and proactively refreshes the token.
  useSessionHeartbeat({
    enabled: !!gd.state.sessionId,
    onSessionExpiring: () => {
      console.log('⚠️ [GD] Session token expiring soon, heartbeat will refresh');
    },
  });

  // Show "Click to submit response" tooltip after 6 seconds of recording
  useEffect(() => {
    if (!isRecording) {
      setShowSubmitHint(false);
      return;
    }
    const timer = setTimeout(() => setShowSubmitHint(true), 6000);
    return () => clearTimeout(timer);
  }, [isRecording]);

  // Fetch feedback when discussion completes
  useEffect(() => {
    if (!gd.state.completed || !gd.state.sessionId) return;
    if (feedbackData || feedbackLoading) return; // Already fetched or in progress

    const fetchFeedback = async () => {
      setFeedbackLoading(true);
      setFeedbackError('');
      const MAX_ATTEMPTS = 15;
      const POLL_INTERVAL_MS = 2000;

      try {
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          }

          const data = await gdApi.sessionFeedback(gd.state.sessionId!);
          if ((data as { status?: string }).status === 'pending' || !data.evaluation) {
            continue;
          }
          if (!data.success) {
            throw new Error((data as { message?: string }).message || 'Failed to load feedback');
          }

          setFeedbackData(data);
          vibeService.trigger('celebration');
          return;
        }

        throw new Error('Feedback is still being generated. Please try again shortly.');
      } catch (err: any) {
        console.error('Failed to fetch GD feedback:', err);
        setFeedbackError(err?.message || 'Failed to load feedback');
        vibeService.trigger('error');
      } finally {
        setFeedbackLoading(false);
      }
    };

    fetchFeedback();
  }, [gd.state.completed, gd.state.sessionId, feedbackData, feedbackLoading]);

  // Rotate tips while waiting for audio to start (session initiated but not connected or no speaker yet)
  useEffect(() => {
    const showLoader = gd.state.sessionId && (!gd.state.connected || !stableSpeaker);
    if (!showLoader) return;
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % preAudioTips.length);
    }, 2500);
    return () => clearInterval(id);
  }, [gd.state.sessionId, gd.state.connected, stableSpeaker, preAudioTips.length]);

  // Debounce current speaker to smooth out rapid audio-driven switches
  useEffect(() => {
    // Clear pending timer
    if (stableSpeakerTimeout.current) {
      clearTimeout(stableSpeakerTimeout.current);
      stableSpeakerTimeout.current = null;
    }

    // If it's the user's turn, lock immediately to user
    if (gd.state.isUserTurn) {
      setStableSpeaker(gd.state.userName || 'user');
      return;
    }

    const nextSpeaker = gd.state.currentSpeaker || null;

    // If backend momentarily clears currentSpeaker, keep the last one to avoid drop-out
    if (!nextSpeaker) {
      return;
    }

    stableSpeakerTimeout.current = setTimeout(() => {
      setStableSpeaker(nextSpeaker);
      stableSpeakerTimeout.current = null;
    }, 400); // 400ms debounce

    return () => {
      if (stableSpeakerTimeout.current) {
        clearTimeout(stableSpeakerTimeout.current);
        stableSpeakerTimeout.current = null;
      }
    };
  }, [gd.state.currentSpeaker, gd.state.isUserTurn, gd.state.userName]);

  // Prevent accidental page refresh / tab close while discussion is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    if (gd.state.sessionId && !gd.state.completed) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gd.state.sessionId, gd.state.completed]);

  const startSession = useCallback(async () => {
    if (!selectedTopic) {
      setStatus('❌ Please select a topic to start the discussion.');
      return;
    }
    if (!userName.trim()) {
      setStatus('❌ Please enter your name.');
      return;
    }
    if (isStarting || gd.state.connected) return;
    setIsStarting(true);
    try {
      await gd.startSession({ token, topic_id: selectedTopic.id, description: userName.trim() });
      await gd.prepareAudio(); // prime audio context after user gesture
    } catch (e) {
      // status already set by hook
    } finally {
      setIsStarting(false);
    }
  }, [userName, gd, isStarting, token, selectedTopic]);

  const sendMessage = useCallback(() => {
    const inputEl = document.getElementById('gd-user-input') as HTMLTextAreaElement | null;
    if (!inputEl) return;
    const text = inputEl.value.trim();
    if (!text || !gd.state.isUserTurn) return;
    gd.sendUserText(text);
    inputEl.value = '';
  }, [gd]);

  const endSession = useCallback(async () => {
    await gd.stopSession();
    await gdApi.abandon();
    setStatus('🔌 Discussion ended');
    navigate('/candidate');
  }, [gd, navigate]);

  const handleEndClick = useCallback(() => {
    setShowEndConfirm(true);
  }, []);

  const handleConfirmEnd = useCallback(async () => {
    setShowEndConfirm(false);
    await endSession();
  }, [endSession]);

  const handleCancelEnd = useCallback(() => {
    setShowEndConfirm(false);
  }, []);

  const startVoiceRecording = useCallback(async () => {
    if (!gd.state.isUserTurn) {
      setStatus('❌ Wait for your turn to speak');
      return;
    }

    // Use backend transcription if browser doesn't support Speech API
    if (useBackendTranscription) {
      return startBackendRecording();
    }

    // Try browser speech recognition first
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      // Fallback to backend
      return startBackendRecording();
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let finalTranscript = '';

      recognition.onstart = () => {
        setIsRecording(true);
        setInterimTranscript('');
        setStatus('🎤 Listening... (speak now)');
        console.log('🎤 Browser speech recognition started');
      };

      recognition.onresult = (event: any) => {
        let interim = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interim += transcript;
          }
        }

        // Show interim results on screen
        setInterimTranscript(interim);
        
        // If we have final transcript, log it
        if (finalTranscript) {
          console.log('Final transcript so far:', finalTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setStatus(`❌ Recognition error: ${event.error}`);
        setIsRecording(false);
        setInterimTranscript('');
      };

      recognition.onend = () => {
        console.log('🎤 Speech recognition ended');
        setIsRecording(false);
        setInterimTranscript('');
        
        // Send final transcript as text if we have any
        if (finalTranscript.trim()) {
          gd.sendUserText(finalTranscript.trim());
          setStatus('✅ Message sent');
        } else {
          setStatus('⚠️ No speech detected');
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
      
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setStatus('❌ Failed to start speech recognition');
    }
  }, [gd, useBackendTranscription]);

  const startBackendRecording = useCallback(async () => {
    try {
      // Phase 1: Notify backend to start recording
      gd.startRecording();

      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });
      
      audioStreamRef.current = stream;

      // Create audio context at 24kHz
      const audioContext = new AudioContext({ sampleRate: 24000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      
      // Use ScriptProcessorNode for processing
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = processor;

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        
        // Convert Float32 PCM to Int16 PCM
        const int16Data = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const sample = Math.max(-1, Math.min(1, inputData[i]));
          int16Data[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        }

        // Convert Int16Array to base64
        const bytes = new Uint8Array(int16Data.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64Audio = window.btoa(binary);

        // Send audio chunk via WebSocket
        gd.sendUserAudio(base64Audio);
      };

      // Connect nodes
      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);
      setStatus('🎤 Recording... (backend transcription)');
      console.log('🎤 Backend audio recording started');
    } catch (error) {
      console.error('Failed to start backend recording:', error);
      setStatus('❌ Microphone access denied');
    }
  }, [gd]);

  const stopVoiceRecording = useCallback(() => {
    // Stop browser speech recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      return;
    }

    // Stop backend audio recording
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    setIsRecording(false);
    
    // Notify backend to process if using backend transcription
    if (useBackendTranscription) {
      gd.stopRecording();
      setStatus('⏳ Processing transcription...');
    }
  }, [gd, useBackendTranscription]);

  const speakerLayout = useMemo(() => {
    const participants = gd.state.participants || [];

    const moderator =
      participants.find((p) => p.type === 'moderator') || {
        id: 'moderator',
        name: 'Luci (Moderator)',
        type: 'moderator' as const,
      };

    const backendHuman = participants.find((p) => p.type === 'human');
    // Always use the locally entered name from the GD input field,
    // not what the backend returns (which may be the user's email).
    const human = backendHuman
      ? { ...backendHuman, name: gd.state.userName || backendHuman.name }
      : { id: 'user', name: gd.state.userName || 'You', type: 'human' as const };

    const aiSpeakers = participants.filter((p) => p.type === 'ai_speaker').slice(0, 3);
    const fallbackAi = [
      { id: 'A', name: 'Participant A', type: 'ai_speaker' as const },
      { id: 'B', name: 'Participant B', type: 'ai_speaker' as const },
      { id: 'C', name: 'Participant C', type: 'ai_speaker' as const },
    ];

    const slots = [0, 1, 2].map((idx) => aiSpeakers[idx] || fallbackAi[idx]);

    const isSpeaking = (p: { id?: string; name?: string }) => {
      const cs = stableSpeaker;
      if (!cs) return false;
      return cs === p.id || cs === p.name;
    };

    const moderatorSpeaking =
      isSpeaking(moderator) ||
      (typeof stableSpeaker === 'string' && /luci|moderator/i.test(stableSpeaker));

    return {
      moderator,
      human,
      aiA: slots[0],
      aiB: slots[1],
      aiC: slots[2],
      speaking: {
        moderator: moderatorSpeaking,
        user: gd.state.isUserTurn || isSpeaking(human),
        a: isSpeaking(slots[0]),
        b: isSpeaking(slots[1]),
        c: isSpeaking(slots[2]),
      },
    };
  }, [gd.state.isUserTurn, gd.state.participants, gd.state.userName, stableSpeaker]);

  const chatMessages = useMemo(() => {
    return gd.state.messages.map((m, idx) => {
      const isUser = m.speaker === 'user';
      const text = isUser ? m.text : `${m.speaker_name}: ${m.text}`;
      return {
        id: `${m.timestamp}-${idx}`,
        sender: isUser ? ('user' as const) : ('ai' as const),
        text,
        timestamp: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
    });
  }, [gd.state.messages]);

  const formatMessageText = (text: string) => {
    // escape basic HTML
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // convert markdown-style bold **text** to <strong>text</strong>
    const withBold = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // preserve newlines
    return withBold.replace(/\n/g, '<br/>');
  };

  // Group topics by difficulty
  const topicsByDifficulty = useMemo(() => {
    const grouped: Record<string, GdTopic[]> = {};
    topics.forEach(topic => {
      if (!grouped[topic.difficulty]) {
        grouped[topic.difficulty] = [];
      }
      grouped[topic.difficulty].push(topic);
    });
    return grouped;
  }, [topics]);

  // Order of difficulties for display
  const difficultyOrder = ['beginner', 'intermediate', 'advanced'];
  const difficultyLabels: Record<string, string> = {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced'
  };


  return (
    <div className="gd-page">
      <div className="gd-background">
        <DotGrid />
      </div>

      {/* End Session Confirm */}
      {showEndConfirm && (
        <div className="gd-end-confirm-overlay">
          <div className="gd-end-confirm-widget">
            <div className="gd-end-confirm-title">End discussion?</div>
            <div className="gd-end-confirm-text">
              Are you sure you want to end the discussion now?
            </div>
            <div className="gd-end-confirm-actions">
              <button className="gd-end-btn secondary" onClick={handleCancelEnd}>Cancel</button>
              <button className="gd-end-btn danger" onClick={handleConfirmEnd}>Yes, end</button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Overlay — shown when session completes */}
      {gd.state.completed && (
        <div className="feedback-overlay-backdrop">
          <div className="feedback-overlay-container">
            {/* Loading state */}
            {feedbackLoading && (
              <div className="feedback-overlay-loading">
                <div className="loading-spinner" />
                <span>Evaluating your performance...</span>
              </div>
            )}

            {/* Error state */}
            {feedbackError && !feedbackLoading && (
              <div className="feedback-overlay-error">
                <span>{feedbackError}</span>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setFeedbackError('');
                      setFeedbackData(null);
                    }}
                  >
                    Retry
                  </button>
                  <button onClick={() => navigate('/candidate')}>Dashboard</button>
                </div>
              </div>
            )}

            {/* Feedback content */}
            {!feedbackLoading && !feedbackError && feedbackData && (
              <GdFeedbackDashboard data={feedbackData} onClose={() => navigate('/candidate')} />
            )}
          </div>
        </div>
      )}

      {/* Reconnecting Banner — shown when WS drops mid-session */}
      {gd.state.reconnecting && !gd.state.completed && (
        <div className="gd-reconnecting-banner">
          <div className="gd-reconnecting-spinner" />
          <span>Connection lost — reconnecting...</span>
        </div>
      )}

      {/* Start Overlay */}
      {(!gd.state.connected && !gd.state.sessionId) && (
        <div className="gd-start-overlay">
          <div className="gd-start-widget">
            <button
              className="gd-start-close"
              aria-label="Back to dashboard"
              onClick={() => navigate('/candidate')}
              title="Back to dashboard"
            >
              ×
            </button>
            <div className="gd-start-title">Group Discussion</div>
            <div className="gd-start-subtitle">Moderator in the center, speakers around the ring. Join when it's your turn.</div>

            {topicsError && (
              <div className="gd-start-error">
                ❌ {topicsError}
              </div>
            )}

            <div className="gd-start-form">
              <div className="gd-input-group">
                <p className="gd-candidate-static" aria-live="polite">
                  <span className="gd-candidate-prefix">Candidate:</span>{' '}
                  <span className="gd-candidate-name">{userName.trim() || '—'}</span>
                </p>
              </div>

              <div className="gd-input-group">
                <label className="gd-input-label">Select Topic</label>
                
                {loadingTopics ? (
                  <div className="gd-topics-loading">Loading topics...</div>
                ) : topics.length === 0 ? (
                  <div className="gd-topics-empty">No topics available</div>
                ) : (
                  <div className="gd-topics-container">
                    {difficultyOrder.map(difficulty => {
                      const difficultyTopics = topicsByDifficulty[difficulty];
                      if (!difficultyTopics || difficultyTopics.length === 0) return null;
                      
                      return (
                        <div key={difficulty} className="gd-difficulty-section">
                          <div className="gd-difficulty-label">
                            {difficultyLabels[difficulty] || difficulty}
                          </div>
                          <div className="gd-topics-grid">
                            {difficultyTopics.map(topic => (
                              <div
                                key={topic.id}
                                className={`gd-topic-card ${selectedTopic?.id === topic.id ? 'selected' : ''}`}
                                onClick={() => setSelectedTopic(topic)}
                                title={topic.description}
                              >
                                <div className="gd-topic-title">{topic.title}</div>
                                {topic.description && (
                                  <div className="gd-topic-description">{topic.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                className="gd-start-btn"
                disabled={isStarting || !selectedTopic || !userName.trim()}
                onClick={startSession}
              >
                {isStarting ? 'Starting...' : 'Start Group Discussion'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`gd-main-container ${(!gd.state.connected && gd.state.sessionId) ? 'gd-connecting' : ''}`}>
        {/* Left: Stage */}
        <div className="gd-stage">
          {(isStarting || (gd.state.sessionId && (!gd.state.connected || !stableSpeaker))) && (
            <div className="gd-preaudio-overlay">
              <div className="gd-preaudio-spinner" />
              <div className="gd-preaudio-text">Preparing audio...</div>
              <div className="gd-preaudio-tip">{preAudioTips[tipIndex]}</div>
            </div>
          )}
          <div className="header-bar">
            <div className="header-left">
              <div className="record-indicator"></div>
              <img src="/image_i_do1.png" alt="Luci logo" className="session-logo" />
              <div className="participants-count">5 Participants</div>
            </div>
            <div className="header-right">
              <div className={`gd-pill ${gd.state.isUserTurn ? 'active' : ''} ${gd.state.reconnecting ? 'reconnecting' : ''}`}>
                {gd.state.completed ? 'Completed' : gd.state.reconnecting ? 'Reconnecting...' : (gd.state.isUserTurn ? 'Your turn' : 'Listening')}
              </div>
            </div>
          </div>

          <div className="gd-topic-row">
            <div className="gd-topic-label">Topic</div>
            <div className="gd-topic-value">
              {typeof gd.state.topic === 'string' 
                ? gd.state.topic 
                : typeof gd.state.topic === 'object' && gd.state.topic !== null
                ? (gd.state.topic as any).title || JSON.stringify(gd.state.topic)
                : selectedTopic?.title || '—'}
            </div>
          </div>

          <div className="gd-wheel-area">
            <div className="gd-wheel" aria-label="Group discussion speaker wheel">
              {/* Petal-shaped segments */}
              <div className={`gd-petal petal-a ${speakerLayout.speaking.a ? 'speaking' : ''}`}>
                <div className="gd-petal-content">
                  <div className="gd-avatar">A</div>
                  <div className="gd-petal-name">{speakerLayout.aiA.name}</div>
                </div>
              </div>

              <div className={`gd-petal petal-b ${speakerLayout.speaking.b ? 'speaking' : ''}`}>
                <div className="gd-petal-content">
                  <div className="gd-avatar">B</div>
                  <div className="gd-petal-name">{speakerLayout.aiB.name}</div>
                </div>
              </div>

              <div className={`gd-petal petal-c ${speakerLayout.speaking.c ? 'speaking' : ''}`}>
                <div className="gd-petal-content">
                  <div className="gd-avatar">C</div>
                  <div className="gd-petal-name">{speakerLayout.aiC.name}</div>
                </div>
              </div>

              <div className={`gd-petal petal-user ${speakerLayout.speaking.user ? 'speaking' : ''}`}>
                <div className="gd-petal-content">
                  <div className="gd-avatar">YOU</div>
                  <div className="gd-petal-name">{speakerLayout.human.name}</div>
                </div>
              </div>

              {/* Center circle - Moderator */}
              <div className={`gd-wheel-center ${speakerLayout.speaking.moderator ? 'speaking' : ''}`}>
                <div className="gd-avatar">L</div>
                <div className="gd-name">Moderator</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="controls-bar">
            <div className="gd-control-tooltip-wrap">
              <span
                className="gd-control-tooltip"
                aria-live="polite"
              >
                {!gd.state.isUserTurn
                  ? 'Wait for your turn'
                  : isRecording
                    ? (showSubmitHint ? 'Click to submit response' : 'Recording...')
                    : 'Click to record response'}
              </span>
              <button
                className={`control-btn ${isRecording ? 'danger' : 'secondary'} ${gd.state.isUserTurn ? 'active' : ''}`}
                disabled={!gd.state.isUserTurn}
                aria-label={
                  !gd.state.isUserTurn
                    ? 'Wait for your turn'
                    : isRecording
                      ? (showSubmitHint ? 'Click to submit response' : 'Recording')
                      : 'Click to record response'
                }
                onClick={() => {
                if (!gd.state.isUserTurn) return;
                if (isRecording) stopVoiceRecording();
                else startVoiceRecording();
              }}
              style={{
                opacity: !gd.state.isUserTurn ? 0.5 : 1,
                cursor: !gd.state.isUserTurn ? 'not-allowed' : 'pointer',
              }}
            >
              {isRecording ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>
            </div>

            <button
              className="control-btn secondary"
              title="Focus input"
              onClick={() => {
                const el = document.getElementById('gd-user-input') as HTMLTextAreaElement | null;
                el?.focus();
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <path d="M6 10h1M10 10h1M14 10h1M18 10h1M8 14h1M12 14h1M16 14h1" />
              </svg>
            </button>

            <button className="control-btn danger" title="End Session" onClick={handleEndClick}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                <line x1="23" y1="1" x2="17" y2="7" />
                <line x1="17" y1="1" x2="23" y2="7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Right: Chat Sidebar (AIInterviewInterface theme) */}
        <div className="chat-sidebar">
          <div className="chat-header">
            <div className="chat-title">Group Discussion Chat</div>
            <div className="question-counter">
              Round {gd.state.round ?? 0} {gd.state.completed ? '(Complete)' : ''}
            </div>
          </div>

          {/* Live Transcription (during recording) */}
          {isRecording && (
            <div className="live-transcription active">
              <div className="transcription-label">Live Transcription</div>
              <div className="transcription-text">
                {interimTranscript || 'Listening...'}
              </div>
            </div>
          )}

          <div className="gd-chat-input-wrap">
            <textarea
              id="gd-user-input"
              className="gd-chat-input"
              placeholder={gd.state.isUserTurn ? 'Share your thoughts...' : 'Wait for your turn'}
              disabled={!gd.state.isUserTurn}
            />
            <button
              className="gd-chat-send"
              disabled={!gd.state.isUserTurn}
              onClick={sendMessage}
              title="Send"
            >
              Send
            </button>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages">
            {chatMessages.map((message) => (
              <div key={message.id} className={`message ${message.sender}`}>
                <div
                  className="message-bubble"
                  dangerouslySetInnerHTML={{ __html: formatMessageText(message.text) }}
                />
                <div className="message-time">{message.timestamp}</div>
              </div>
            ))}
            {chatMessages.length === 0 && (
              <div className="gd-chat-empty">Waiting for moderator intro...</div>
            )}
          </div>

          {/* Status Bar */}
          <div className="status-bar">
            <div className="status-dot"></div>
            <div>{status}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDiscussion;
